import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { useFirestore } from './FirestoreContext';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import cmtService from '../services/cmtService';
import { 
  isAdminRole, 
  isLeaderRole, 
  isLeaderAltRole,
  validateRole 
} from '../models/role.js';

/**
 * 🏗️ Multi-CMT Context Provider
 * 
 * Enhanced context that supports multiple CMTs per organization while
 * maintaining backward compatibility with existing single-CMT setups.
 * 
 * @author Crisis Compass Development Team
 * @version 1.0.0 - Phase 1 Multi-CMT Implementation
 */

const MultiCmtContext = createContext();

export function useMultiCmt() {
  return useContext(MultiCmtContext);
}

export function MultiCmtProvider({ children }) {
  const { currentUser } = useAuth();
  const { db } = useFirestore();
  
  // Multi-CMT State
  const [activeCmt, setActiveCmt] = useState(null);           // Currently selected CMT
  const [availableCmts, setAvailableCmts] = useState([]);     // All CMTs user has access to
  const [defaultCmt, setDefaultCmt] = useState(null);         // Organization's default CMT
  const [userCmtRoles, setUserCmtRoles] = useState(new Map()); // User's role in each CMT
  
  // 🏗️ PHASE 1: Hierarchy State
  const [cmtHierarchy, setCmtHierarchy] = useState(null);     // Hierarchical CMT structure
  const [globalCmt, setGlobalCmt] = useState(null);           // Global/Parent CMT
  const [hasHierarchy, setHasHierarchy] = useState(false);    // Whether org has hierarchical structure
  
  // Legacy compatibility state
  const [isCmtLeader, setIsCmtLeader] = useState(false);
  const [isCmtLeaderAlternate, setIsCmtLeaderAlternate] = useState(false);
  const [isCmtAdmin, setIsCmtAdmin] = useState(false);
  
  // UI State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * 🔄 Initialize Multi-CMT context when user changes
   */
  useEffect(() => {
    const initializeMultiCmt = async () => {
      if (!currentUser?.orgId) {
        // Reset state when no user
        setActiveCmt(null);
        setAvailableCmts([]);
        setDefaultCmt(null);
        setUserCmtRoles(new Map());
        setIsCmtLeader(false);
        setIsCmtLeaderAlternate(false);
        setIsCmtAdmin(false);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Get user data for CMT assignments and role validation
        const userDoc = await getDoc(doc(db, 'c3_users', currentUser.uid));
        if (!userDoc.exists()) {
          throw new Error('User not found');
        }

        const userData = userDoc.data();
        
        // Validate and normalize organizational role
        const normalizedRole = validateRole(userData.role);
        setIsCmtAdmin(isAdminRole(normalizedRole));

        // Get all CMTs for the organization
        const orgCmts = await cmtService.getOrganizationCMTs(currentUser.orgId);
        
        // 🏗️ PHASE 1: Load hierarchy structure
        const hierarchyData = await cmtService.getCMTHierarchy(currentUser.orgId);
        setCmtHierarchy(hierarchyData);
        setHasHierarchy(hierarchyData.hasHierarchy);
        setGlobalCmt(hierarchyData.globalCmt);
        
        // Get organization's default CMT
        const orgDefaultCmt = await cmtService.getDefaultCMT(currentUser.orgId);
        setDefaultCmt(orgDefaultCmt);

        // 🎯 MODERNIZED: Get CMT memberships from cmtMembers collection ONLY
        const userAccessibleCmts = [];
        const roleMap = new Map();
        // Note: orgCmtById removed as no longer needed for hard cutover

        // Check all organization CMTs for user membership
        console.log(`🔍 Checking CMT memberships for user ${currentUser.uid} in org ${currentUser.orgId}`);
        console.log(`🔍 Organization CMTs found:`, orgCmts.map(c => ({ id: c.id, name: c.name })));
        
        for (const cmt of orgCmts) {
          try {
            const membershipRef = doc(db, 'cmtMembers', cmt.id, 'users', currentUser.uid);
            console.log(`🔍 Checking membership path: cmtMembers/${cmt.id}/users/${currentUser.uid}`);
            const membershipDoc = await getDoc(membershipRef);
            
            if (membershipDoc.exists()) {
              const membershipData = membershipDoc.data();
              console.log(`🔍 Found membership document for ${cmt.name}:`, membershipData);
              
              // Only include active memberships
              if (!membershipData.status || membershipData.status === 'active') {
                userAccessibleCmts.push(cmt);
                roleMap.set(cmt.id, membershipData.role || 'member');
                console.log(`✅ Found active membership: ${cmt.name} (${membershipData.role})`);
              } else {
                console.log(`❌ Inactive membership for ${cmt.name}: status = ${membershipData.status}`);
              }
            } else {
              console.log(`❌ No membership document found for ${cmt.name}`);
            }
          } catch (error) {
            console.warn(`❌ Failed to check membership for CMT ${cmt.id}:`, error);
          }
        }

        // Admin users get access to all CMTs
        if (isAdminRole(normalizedRole)) {
          for (const cmt of orgCmts) {
            if (!userAccessibleCmts.find(c => c.id === cmt.id)) {
              userAccessibleCmts.push(cmt);
              roleMap.set(cmt.id, 'Admin');
            }
          }
        }

        // 🔧 CRITICAL FIX: Create new array/map references to trigger React re-renders
        setAvailableCmts([...userAccessibleCmts]);
        setUserCmtRoles(new Map(roleMap));

        // Determine active CMT from fresh, validated list
        let initialActiveCmt = null;

        // 1. Try user's saved preference
        if (userData.currentCmtId) {
          initialActiveCmt = userAccessibleCmts.find(c => c.id === userData.currentCmtId);
        }

        // 2. Fall back to default CMT if user has access
        if (!initialActiveCmt && orgDefaultCmt) {
          initialActiveCmt = userAccessibleCmts.find(c => c.id === orgDefaultCmt.id);
        }

        // 🐛 DEBUG: CMT Selection Logic
        console.log('🐛 CMT INITIALIZATION DEBUG:', {
          userId: currentUser.uid,
          orgId: currentUser.orgId,
          userAccessibleCmts: userAccessibleCmts.map(c => ({ id: c.id, name: c.name })),
          userSavedPreference: userData.currentCmtId,
          orgDefaultCmt: orgDefaultCmt?.id,
          initialActiveCmt: initialActiveCmt?.id
        });

        // 3. Fall back to first accessible CMT (if only one, set; if multiple, leave for picker)
        if (!initialActiveCmt && userAccessibleCmts.length > 0) {
          if (userAccessibleCmts.length === 1) {
            initialActiveCmt = userAccessibleCmts[0];
          }
        }

        if (initialActiveCmt) {
          await setActiveCMT(initialActiveCmt, false, userAccessibleCmts, roleMap); // Pass fresh CMT list and role map
        }

        console.log('✅ Multi-CMT context initialized:', {
          activeCmt: initialActiveCmt?.name,
          availableCmts: userAccessibleCmts.length,
          defaultCmt: orgDefaultCmt?.name,
          hasHierarchy: hierarchyData.hasHierarchy,
          globalCmt: hierarchyData.globalCmt?.name,
          childCmts: hierarchyData.childCmts?.length || 0
        });



      } catch (error) {
        console.error('❌ Failed to initialize Multi-CMT context:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    initializeMultiCmt();
  }, [currentUser?.uid, currentUser?.orgId, db]);

  /**
   * 🎯 Set active CMT with role-based permissions
   */
  const setActiveCMT = async (cmt, saveToDatabase = true, freshAvailableCmts = null, freshRoleMap = null) => {
    try {
      if (!cmt) {
        setActiveCmt(null);
        setIsCmtLeader(false);
        setIsCmtLeaderAlternate(false);
        return;
      }

      // Verify user has access to this CMT against the freshly validated list
      const cmtsToCheck = freshAvailableCmts || availableCmts;
      const hasAccess = cmtsToCheck.some(c => c.id === cmt.id);
      if (!hasAccess) {
        console.warn('Attempted to set inactive/invalid CMT. Falling back gracefully.');
        // Graceful fallback: if exactly one accessible CMT, auto-select it; otherwise, leave null so picker can prompt
        if (cmtsToCheck.length === 1) {
          const only = cmtsToCheck[0];
          // 🔧 CRITICAL FIX: Ensure fresh object reference to trigger re-renders
          setActiveCmt({...only});
          const roleMapToUse = freshRoleMap || userCmtRoles;
          const onlyRole = roleMapToUse.get(only.id);
          setIsCmtLeader(isLeaderRole(onlyRole));
          setIsCmtLeaderAlternate(isLeaderAltRole(onlyRole));
          if (saveToDatabase && currentUser?.uid) {
            await updateDoc(doc(db, 'c3_users', currentUser.uid), {
              currentCmtId: only.id,
              updatedAt: new Date()
            });
          }
          console.log(`🎯 Active CMT set to only accessible team: ${only.name} (Role: ${onlyRole}) [source: ${freshRoleMap ? 'fresh' : 'state'}]`);
          return only;
        }
        // Multiple or zero options: do not throw; allow UI to render selector or empty state
        setActiveCmt(null);
        setIsCmtLeader(false);
        setIsCmtLeaderAlternate(false);
        return null;
      }

      // 🔧 CRITICAL FIX: Ensure fresh object reference to trigger re-renders
      setActiveCmt({...cmt});

      // Set role-based permissions for the active CMT
      // 🔧 SURGICAL FIX: Use fresh role map to avoid React state race condition
      const roleMapToUse = freshRoleMap || userCmtRoles;
      const userRole = roleMapToUse.get(cmt.id);
      setIsCmtLeader(isLeaderRole(userRole));
      setIsCmtLeaderAlternate(isLeaderAltRole(userRole));

      // Save user's CMT preference to database
      if (saveToDatabase && currentUser?.uid) {
        await updateDoc(doc(db, 'c3_users', currentUser.uid), {
          currentCmtId: cmt.id,
          updatedAt: new Date()
        });
      }

      console.log(`🎯 Active CMT set to: ${cmt.name} (Role: ${userRole}) [source: ${freshRoleMap ? 'fresh' : 'state'}]`);
      return cmt;

    } catch (error) {
      console.error('❌ Failed to set active CMT:', error);
      // Do not throw; allow UI to recover via selector
      setError(error?.message || 'Failed to set active CMT');
      return null;
    }
  };

  /**
   * 🔄 Switch to a different CMT
   */
  const switchCMT = async (cmtId) => {
    try {
      setLoading(true);

      const targetCmt = availableCmts.find(c => c.id === cmtId);
      if (!targetCmt) {
        throw new Error('CMT not found or access denied');
      }

      await setActiveCMT(targetCmt, true);
      return targetCmt;

    } catch (error) {
      console.error('❌ Failed to switch CMT:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * 🔄 Refresh CMT data (useful after creating/updating CMTs)
   */
  const refreshCMTs = async () => {
    try {
      if (!currentUser?.orgId) return;

      setLoading(true);
      
      // Refresh organization CMTs
      const orgCmts = await cmtService.getOrganizationCMTs(currentUser.orgId);
      
      // Refresh default CMT
      const orgDefaultCmt = await cmtService.getDefaultCMT(currentUser.orgId);
      setDefaultCmt(orgDefaultCmt);

      // Update available CMTs while preserving access rights
      const refreshedAvailableCmts = availableCmts.map(userCmt => {
        const updated = orgCmts.find(orgCmt => orgCmt.id === userCmt.id);
        return updated || userCmt;
      });

      // 🔧 CRITICAL FIX: Create new array reference to trigger React re-renders
      setAvailableCmts([...refreshedAvailableCmts]);

      // Update active CMT if it's in the refreshed list
      if (activeCmt) {
        const refreshedActiveCmt = refreshedAvailableCmts.find(c => c.id === activeCmt.id);
        if (refreshedActiveCmt) {
          // 🔧 CRITICAL FIX: Ensure fresh object reference to trigger re-renders
          setActiveCmt({...refreshedActiveCmt});
        }
      }

      console.log('🔄 CMT data refreshed');

    } catch (error) {
      console.error('❌ Failed to refresh CMT data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 🎯 Get user's role in specific CMT
   */
  const getUserRoleInCMT = (cmtId) => {
    return userCmtRoles.get(cmtId) || null;
  };

  /**
   * ✅ Check if user has specific permission in active CMT
   */
  const hasPermission = (permission) => {
    if (!activeCmt) return false;
    if (isCmtAdmin) return true; // Admins have all permissions

          // Note: userRole removed as permissions now handled via membership
    
    switch (permission) {
      case 'manage_incidents':
        return activeCmt.permissions?.canManageIncidents && (isCmtLeader || isCmtLeaderAlternate);
      case 'manage_tasks':
        return activeCmt.permissions?.canManageTasks && (isCmtLeader || isCmtLeaderAlternate);
      case 'manage_members':
        return activeCmt.permissions?.canManageMembers && isCmtAdmin;
      case 'view_reports':
        return activeCmt.permissions?.canViewReports;
      default:
        return false;
    }
  };

  /**
   * 🎯 Get CMT-scoped query filter for database operations
   */
  const getCMTQueryFilter = () => {
    if (!activeCmt) return null;
    
    return {
      orgId: currentUser?.orgId,
      cmtId: activeCmt.id
    };
  };

  // 🔧 CRITICAL FIX: Context value with proper identity tracking for re-renders
  const value = useMemo(() => ({
    // New Multi-CMT API
    activeCmt,
    activeCmtId: activeCmt?.id ?? null,  // Convenience primitive for easier deps
    availableCmts,
    defaultCmt,
    userCmtRoles,
    setActiveCMT,
    switchCMT,
    refreshCMTs,
    getUserRoleInCMT,
    hasPermission,
    getCMTQueryFilter,
    
    // 🏗️ PHASE 1: Hierarchy API
    cmtHierarchy,
    globalCmt,
    hasHierarchy,
    
    // Legacy compatibility (maps to active CMT)
    currentCmt: activeCmt,          // For backward compatibility
    accessibleCmts: availableCmts,  // For backward compatibility
    isCmtLeader,
    isCmtLeaderAlternate,
    isCmtAdmin,
    
    // UI State
    loading,
    error,
    
    // Utility
    isMultiCmtEnabled: availableCmts.length > 1
  }), [
    activeCmt, 
    availableCmts, 
    defaultCmt, 
    userCmtRoles, 
    cmtHierarchy, 
    globalCmt, 
    hasHierarchy, 
    isCmtLeader, 
    isCmtLeaderAlternate, 
    isCmtAdmin, 
    loading, 
    error
  ]);

  return (
    <MultiCmtContext.Provider value={value}>
      {children}
    </MultiCmtContext.Provider>
  );
}

// 🎯 DEPRECATED: Legacy hook - use useMultiCmt() instead
export function useCmt() {
  console.warn('⚠️ DEPRECATED: useCmt() is deprecated. Use useMultiCmt() instead.');
  const context = useContext(MultiCmtContext);
  if (!context) {
    console.warn('useCmt called outside MultiCmtProvider - falling back to basic structure');
    return {
      currentCmt: null,
      accessibleCmts: [],
      isCmtLeader: false,
      isCmtLeaderAlternate: false,
      isCmtAdmin: false,
      loading: false,
      switchCmt: async () => {}
    };
  }
  
  // Return legacy-compatible object
  return {
    currentCmt: context.activeCmt,
    accessibleCmts: context.availableCmts,
    isCmtLeader: context.isCmtLeader,
    isCmtLeaderAlternate: context.isCmtLeaderAlternate,
    isCmtAdmin: context.isCmtAdmin,
    loading: context.loading,
    switchCmt: context.switchCMT
  };
}

export default MultiCmtContext;